// @flow

import React, { useContext, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Cookie from 'js-cookie';
import { Button, Icon, Heading, Paragraph } from 'operation-diameter';

import CurrencyInput from 'shared/components/CurrencyInput';

import { useCopy } from 'hooks/useCopy';
import { useForm } from 'hooks/useForm';
import { INCREASE_LOAN_AMOUNT_TO_750K } from 'shared/config';
import { FormContext } from 'shared/formContext/FormContext';
import { OptimizelyExperiment } from 'shared/optimizely/OptimizelyExperiment';
import LoanTerm from 'shared/components/LoanTerm';
import { TrackingContext } from 'shared/tracking/TrackingContext';
import {
  nonEmpty,
  loanAmount as loanAmountValidation,
  increasedLoanAmount as increasedLoanAmountValidation
} from 'shared/validations';

import { formatCurrencyInputOutput } from 'shared/utils/helpers';

import styles from './LoanAmountAndTerm.module.css';
import Divider from 'shared/components/Divider';

const ACQUIRE_PRODUCT_JOURNEY_URL_KEY = 'product_journey';

/* eslint-disable no-unused-vars */
const LOAN_AMOUNT_NOTICE_EXPERIMENT = '22539251446';
const VARIATION_TO_SHOW_NOTICE = '22563470338';
/* eslint-enable no-unused-vars */

const LoanAmountAndTerm = () => {
  useEffect(() => {
    /* To prevent users who already have passed eligibility once getting stuck
     in a loop. This happens when users have once passed eligibility, navigated
     away from the application and then come back to it. */
    if (Cookie.get('gpo-application-id')) {
      console.log(
        'User has already passed eligibility once - redirecting to loan-application'
      );
      window.location.assign('/uk/borrower/loan-application');
    }
  }, []);

  const { push } = useHistory();
  const location = useLocation();
  const eligibility = useContext(FormContext);
  const { setCustomDimensionValue } = useContext(TrackingContext);
  const { getCopy } = useCopy('borrower');

  const productJourneyParam = new URLSearchParams(location.search).get(
    ACQUIRE_PRODUCT_JOURNEY_URL_KEY
  );

  const productJourney = useMemo(
    () =>
      productJourneyParam === 'assetfinance'
        ? 'assetfinance10'
        : 'originations10',
    [productJourneyParam]
  );

  const fields = {
    loanAmount: {
      initialState: eligibility.fields.loanAmount,
      validations: [
        INCREASE_LOAN_AMOUNT_TO_750K
          ? increasedLoanAmountValidation
          : loanAmountValidation
      ]
    },
    loanTerm: {
      initialState: eligibility.fields.loanTerm,
      validations: [nonEmpty],
      validateOnSet: true
    }
  };

  const { loanAmount, loanTerm, isFormValid, runValidations } = useForm({
    fields
  });

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const fieldValues = {
      loanAmount: loanAmount.value,
      loanTerm: loanTerm.value,
      introducerId: null,
      productJourney
    };

    const introducerIdCookie = Cookie.get('introducer_id');
    if (introducerIdCookie) {
      fieldValues.introducerId = introducerIdCookie;
    }

    if (isFormValid) {
      eligibility.setFields(fieldValues);

      setCustomDimensionValue('amountRequested', loanAmount.value);

      push('/business-details');
    } else {
      runValidations();
    }
  };

  return (
    <div
      className={styles.loanAmountAndTermWrapper}
      data-testid='control-variant'>
      <section className={styles.wrapper}>
        <Heading headingSize='h3'>{getCopy('eligibility.title')}</Heading>
        {eligibility.fields.first_name ? (
          <Paragraph paragraphSize='p1' paragraphWeight='500'>
            Welcome, {eligibility.fields.first_name}. Because you connected your
            Sage account, we&apos;ve been able to pre-fill some of the
            application questions for you.
          </Paragraph>
        ) : null}
        <Paragraph paragraphSize='p1' paragraphWeight='500'>
          {getCopy('eligibility.subtitle')}
        </Paragraph>
        <div className={styles.highlightText}>
          <Icon icon='success' isDecorative />
          <Paragraph paragraphSize='p2'>
            {getCopy('eligibility.does-not-affect-credit-score')}
          </Paragraph>
        </div>
      </section>
      <Divider />
      <section>
        <Heading headingSize='h6'>
          {getCopy('eligibility.loan-amount.title')}
        </Heading>
        <form
          className={styles.form}
          onSubmit={(e) => {
            handleSubmit(e);
          }}
          name='loan-amount-and-term'>
          <OptimizelyExperiment id={LOAN_AMOUNT_NOTICE_EXPERIMENT}>
            {(variation) => (
              <>
                {variation === VARIATION_TO_SHOW_NOTICE && (
                  <Paragraph>
                    We may be able to offer you up to 40% of your most recent
                    annual turnover
                  </Paragraph>
                )}
                <CurrencyInput
                  label={getCopy('eligibility.loan-amount.label')}
                  helpText={getCopy(
                    INCREASE_LOAN_AMOUNT_TO_750K
                      ? 'eligibility.loan-amount.status_increased'
                      : 'eligibility.loan-amount.status'
                  )}
                  name='loan-amount'
                  value={loanAmount.value.toString()}
                  onChange={(event) =>
                    loanAmount.set(
                      formatCurrencyInputOutput(
                        event.currentTarget.value
                      ).toString()
                    )
                  }
                  onBlur={loanAmount.validate}
                  {...(loanAmount.error && {
                    error: getCopy(
                      INCREASE_LOAN_AMOUNT_TO_750K
                        ? 'eligibility.loan-amount.status_increased'
                        : 'eligibility.loan-amount.status'
                    )
                  })}
                  additionalClassnames={styles.currencyInput}
                />
              </>
            )}
          </OptimizelyExperiment>

          <LoanTerm loanTermField={loanTerm} />

          <Button
            type='submit'
            size='large'
            additionalClassnames={styles.submitButton}
            data-ga-category='eligibility'
            data-ga-action='submit_loan_amount'
            data-ga-label='loan_amount'
            data-cy='continue-button'>
            Continue
          </Button>
        </form>
      </section>
    </div>
  );
};

export default LoanAmountAndTerm;
